import { styled } from "leva/plugin";
import { useContext, useEffect, useState } from "react";
import SliderContext from "../context/SliderContext";

const SliderContainer = styled("div", {
	display: "flex",
	flexDirection: "column",
	minWidth: "86px",
	opacity: 1,
	transition: "opacity 100ms",
	"&.dragging": {
		opacity: 0,
	},
});

const LabelRow = styled("div", {
	display: "flex",
	justifyContent: "space-between",
	alignItems: "center",
	marginBottom: "6px",
});

const Label = styled("label", {
	fontSize: "10px",
	color: "$highlight2",
});

const InputValueDisplay = styled("input", {
	fontSize: "10px",
	color: "$highlight1",
	backgroundColor: "transparent",
	width: "auto",
	textAlign: "right",
	border: "1px solid transparent",
	outline: "none",
	"&::-webkit-inner-spin-button, &::-webkit-outer-spin-button": {
		"-webkit-appearance": "none",
		appearance: "none",
		margin: 0,
	},
	"&:focus": {
		outline: "none",
		border: "1px solid $highlight1",
		borderRadius: "4px",
	},
});

const ValueDisplay = styled("span", {
	fontSize: "10px",
	color: "$highlight1",
	cursor: "text",
	border: "1px solid transparent",
});

const SliderTrack = styled("div", {
	width: "100%",
	height: "1px",
	backgroundColor: "$highlight1",
	position: "relative",
});

const SliderThumb = styled("div", {
	width: "12px",
	height: "12px",
	border: "2px solid $highlight3",
	borderRadius: "50%",
	backgroundColor: "transparent",
	position: "absolute",
	top: "50%",
	transform: "translateY(-50%)",
	transition: "transform 100ms", // Add transition for hover animation
});

const SliderThumbCover = styled("div", {
	width: "6px",
	height: "6px",
	backgroundColor: "$elevation3", // Use the same color as the background: ;
	borderRadius: "50%",
	position: "absolute",
	top: "50%",
	left: "50%",
	transform: "translate(-50%, -50%)",
});

interface InputSliderProps {
	label: string;
	value: number;
	min: number;
	max: number;
	step: number;
	onChange: (value: any) => void;
}

const InputSlider: React.FC<InputSliderProps> = ({
	label,
	value,
	min,
	max,
	step,
	onChange,
}) => {
	const [editingValue, setEditingValue] = useState(false);
	const [editingDisplayValue, setEditingDisplayValue] = useState(value);
	const isBezierHandleSlider =
		label === "In X" ||
		label === "In Y" ||
		label === "Out X" ||
		label === "Out Y";

	const percentage = ((value - min) / (max - min)) * 100;
	const { isDragging, setIsDragging } = useContext(SliderContext);
	const [thisIsDragging, setThisIsDragging] = useState(false);

	const handleValueDisplayClick = () => {
		setEditingDisplayValue(parseFloat(value.toFixed(2)));
		setThisIsDragging(true);
		setIsDragging(true);
		setEditingValue(true);
	};

	const handleValueInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.value === "") {
			e.target.value = "0";
			onChange(e);
		}
		const inputValue = e.target.valueAsNumber;
		if (!Number.isNaN(inputValue) && inputValue >= min && inputValue <= max) {
			onChange(e);
			setEditingDisplayValue(inputValue);
		}
	};

	// useEffect(() => {
	//   console.log("value changed");
	// }, [value]);

	const handleValueInputBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
		onChange(e);
		setEditingValue(false);
		setThisIsDragging(false);
		setIsDragging(false);
	};

	return (
		<SliderContainer
			className={isDragging && !thisIsDragging ? "dragging" : ""}
		>
			<LabelRow>
				<Label>{label}</Label>
				{editingValue ? (
					<InputValueDisplay
						type="number"
						value={editingDisplayValue}
						min={min}
						max={max}
						step={step}
						onChange={handleValueInputChange}
						onBlur={handleValueInputBlur}
						onKeyPress={(event) => {
							if (event.key === "Enter") {
								setEditingValue(false);
								setThisIsDragging(false);
								setIsDragging(false);
							}
						}}
						autoFocus
					/>
				) : (
					<ValueDisplay onClick={handleValueDisplayClick}>
						{isBezierHandleSlider ? value.toFixed(2) : value}
					</ValueDisplay>
				)}
			</LabelRow>
			<SliderTrack>
				<input
					type="range"
					value={value}
					min={min}
					max={max}
					step={step}
					onChange={onChange}
					onMouseDown={(event) => {
						event.stopPropagation();
						setThisIsDragging(true);
						setIsDragging(true);
					}}
					onMouseUp={(event) => {
						event.stopPropagation();
						setThisIsDragging(false);
						setIsDragging(false);
					}}
					style={{
						width: "100%",
						height: "24px", // Increase height for larger hitbox
						position: "absolute",
						zIndex: 2,
						opacity: 0,
						cursor: "pointer",
						marginTop: "-11px", // Vertically center the hitbox
					}}
				/>
				<SliderThumb style={{ left: `calc(${percentage}% - 6px)` }}>
					<SliderThumbCover />
				</SliderThumb>
			</SliderTrack>
			<style>{`
        input[type="range"]:hover + div {
          transform: translateY(-50%) scale(1.2);
        }
      `}</style>
		</SliderContainer>
	);
};

export default InputSlider;
